import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { ComboboxOption } from '@/components/ui/Combobox';
import type { MasterRef, Paginated, ReferenceListParams, ReferenceRecord } from '@/lib/types';

/**
 * The four picklists on a deliverable — region, booking status, travel package
 * and ads plan — are reference-master tables, not string enums. You write
 * `regionId` and read back `region: { id, name }`, so the read key and the write
 * key are never the same string.
 *
 * Never hard-code these options: they are database rows, and a hard-coded list
 * goes stale the first time somebody adds a value.
 */
export type ReferenceKind = 'regions' | 'bookingStatuses' | 'travelPackages' | 'adsPlans';

/**
 * One parameterised endpoint mounted four times, so one hook covers all four.
 * Written as arrow wrappers rather than bare method references so `this` stays
 * bound to `api`.
 */
const LISTERS: Record<
  ReferenceKind,
  (params: ReferenceListParams, signal: AbortSignal) => Promise<Paginated<ReferenceRecord>>
> = {
  regions: (params, signal) => api.listRegions(params, signal),
  bookingStatuses: (params, signal) => api.listBookingStatuses(params, signal),
  travelPackages: (params, signal) => api.listTravelPackages(params, signal),
  adsPlans: (params, signal) => api.listAdsPlans(params, signal),
};

/*
 * `perPage: 200` is the endpoint's ceiling, and asking for it is deliberate:
 * the default page is 50, the largest master today holds 21 rows, and one page
 * is therefore genuinely the whole list. If a master ever outgrows 200 this
 * hook starts hiding values silently, which is why the ceiling is requested
 * rather than the default accepted.
 *
 * `isActive: true` is equally deliberate. Unfiltered, the endpoint returns
 * retired rows too, and a retired id cannot be saved — offering one in a picker
 * is offering a guaranteed 422.
 */
const QUERY: ReferenceListParams = { page: 1, perPage: 200, isActive: true };

/**
 * Loads the active rows of one reference master for a picker.
 *
 * Rows arrive in curated picker order (`sortOrder` ASC, then `name` ASC) and are
 * returned untouched — do not re-sort them alphabetically at the call site.
 *
 * `failed` is surfaced rather than swallowed: a picker that silently shows
 * nothing is worse than one that says it could not load, because "no options"
 * and "the options did not arrive" look identical to a user and only one of
 * them is worth retrying.
 */
export function useReferenceOptions(kind: ReferenceKind, enabled = true) {
  const [records, setRecords] = useState<ReferenceRecord[]>([]);
  // Starts true when enabled so the first paint shows a loading picker rather
  // than an empty one — and so nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  /*
   * If the caller switches which master it wants, drop the previous one's rows
   * during render rather than after the next fetch resolves. Without this the
   * picker briefly offers regions where booking statuses belong — and worse,
   * `withStoredOption` below would read that stale, non-empty list as evidence
   * that the stored value is retired. This is React's documented
   * adjust-state-when-props-change pattern; it re-renders before paint, so the
   * wrong list is never shown.
   */
  const [loadedKind, setLoadedKind] = useState(kind);
  if (loadedKind !== kind) {
    setLoadedKind(kind);
    setRecords([]);
    setLoading(enabled);
    setFailed(false);
  }

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    LISTERS[kind](QUERY, controller.signal)
      .then((result) => {
        setRecords(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Keep `records` empty on failure — but say so, so the caller can render
        // an error instead of an innocent-looking empty list.
        setRecords([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [kind, enabled]);

  return { records, loading, failed };
}

/** `region.name` is `NOT NULL` but can in principle be `''` — fall back to the id. */
function labelFor(id: number, name: string): string {
  const trimmed = name.trim();
  return trimmed === '' ? `#${id}` : trimmed;
}

/**
 * Builds Combobox options from the active master rows, injecting the record's
 * stored value when it is not among them.
 *
 * THE DEACTIVATION TRAP LIVES HERE. It is the single most likely bug in this
 * surface, and it is solved once, in this function, for the whole app.
 *
 * Masters are deactivated, never deleted, so history is never rewritten. That
 * cuts two ways and both halves are load-bearing:
 *
 * - **On read there is no `isActive` filter.** A deliverable pointing at a
 *   region retired last year still reads back `region: { id, name }` fully
 *   populated. Nothing on the embedded object marks it retired — there is no
 *   `isActive` flag there — so the only way to know is to cross-reference the
 *   master list, which is exactly what this function does.
 * - **On write that same id is rejected** with
 *   `422 Region 8 does not exist or is not active`, even when nothing about the
 *   record changed.
 *
 * So a Combobox fed only the `isActive=true` rows does not contain the stored
 * value. It renders blank, or resets to the first option — which then *is* sent
 * and *is* accepted, silently replacing a value the user never looked at. This
 * function prevents that by prepending the stored `{ id, name }` as a
 * selected-but-not-listed option, hinted so the user can see it is no longer on
 * offer.
 *
 * Injecting the option is only half the fix. The other half belongs to the form:
 * **send a picklist id only when the user actually changed it.** Re-sending an
 * unchanged, since-retired id is a 422 naming a field the user never touched.
 *
 * @param records The active rows, in received (curated) order.
 * @param stored  The value on the record being edited, or `null` when creating.
 */
export function withStoredOption(
  records: ReferenceRecord[],
  stored: MasterRef | null,
): ComboboxOption[] {
  const options: ComboboxOption[] = records.map((record) => ({
    value: String(record.id),
    label: labelFor(record.id, record.name),
  }));

  if (!stored) return options;
  if (records.some((record) => record.id === stored.id)) return options;

  /*
   * The stored id is not in the active list. Two different situations look
   * identical from here, and conflating them would lie to the user:
   *
   * - `records` is non-empty  -> the list really did load, and this value's
   *   absence from it is evidence that it was retired. Say so.
   * - `records` is empty      -> the list is still loading, or failed to load.
   *   Absence proves nothing, so inject the value with no hint. The picker
   *   still shows what the record actually holds instead of going blank, and it
   *   does not accuse a perfectly live value of being retired for the frame or
   *   two before the rows arrive.
   */
  const knownRetired = records.length > 0;

  return [
    {
      value: String(stored.id),
      label: labelFor(stored.id, stored.name),
      ...(knownRetired ? { hint: 'No longer offered' } : {}),
    },
    ...options,
  ];
}
